import React, { useEffect, useRef } from 'react';
import { Props as ExtraFieldsAreaProps } from './ExtraFieldsArea';
import { ExtraFieldsArea } from './ExtraFieldsArea';
import { FormContext, useForm } from 'react-hook-form';
import {
  ExtraFieldInput,
  ExtraFieldValueShortFragment,
} from '../../../__generated__/graphql';
import {
  collectFormDataExtraFields,
  getFormValuesFromExtraFields,
  recordTypeFormKey,
} from '../logic';
import { isEqual } from 'lodash';

type Props = ExtraFieldsAreaProps & {
  onChange: (
    extraFields: ExtraFieldInput[],
    recordTypeName: string | undefined,
  ) => void;
  values?: readonly ExtraFieldValueShortFragment[];
  recordTypeName: string | null; // null means no recordType
};

// ExtraFieldsArea use inside 'react-hook-form'. That class hides these implementation details. And allows to use the component as normal react.
// That implementation is puzzled, better to rewrite ExtraFieldsArea not to use 'react-hook-form'.
export const ExtraFieldsAreaSimple: React.FC<Props> = ({
  onChange,
  values = [],
  recordTypeName,
  ...restProps
}) => {
  const formValues = getFormValuesFromExtraFields(values, recordTypeName);
  // we need to store last properties to understand then we need to reset
  const initialFormValues = useRef<Record<string, any>>(formValues);
  // reset in progress flag
  const resetingToValues = useRef<Record<string, any> | undefined>(undefined);

  const formMethods = useForm({
    defaultValues: initialFormValues.current,
  });

  const { watch, reset } = formMethods;

  const watchedValues = watch();

  // we always need to call form.reset if values were changed externally. Because 'react-hook-form' has some internal state...
  // resetingToValues flag used to avoid onChange event during the reset
  useEffect(() => {
    if (!isEqual(formValues, initialFormValues.current)) {
      initialFormValues.current = formValues;
      resetingToValues.current = formValues;
      reset(formValues);
    }
  }, [reset, formValues]);

  useEffect(() => {
    if (
      resetingToValues.current !== undefined &&
      !isEqual(watchedValues, resetingToValues.current)
    ) {
      // form is not been reset yet, we can't raise onChange event
      return;
    }
    resetingToValues.current = undefined;
    if (!isEqual(watchedValues, formValues)) {
      initialFormValues.current = watchedValues; // we need this to not call reset second time
      const extraFieldInputs = collectFormDataExtraFields(watchedValues);
      const recordTypeName = watchedValues[recordTypeFormKey];
      onChange(extraFieldInputs, recordTypeName);
    }
  }, [watchedValues, onChange, formValues]);

  return (
    <FormContext {...formMethods}>
      <ExtraFieldsArea {...restProps} />
    </FormContext>
  );
};
